<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import {
  toneClassesFor,
  DEFAULT_CHANNEL_TONE,
} from 'dashboard/helper/channelTone';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    required: true,
  },
  // Soft tint applied to the icon tile so each provider keeps a stable identity
  // across the catalogue and the connected list.
  tone: {
    type: String,
    default: DEFAULT_CHANNEL_TONE,
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
  isBeta: {
    type: Boolean,
    default: false,
  },
  hasVoiceBadge: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const tileClasses = computed(() => toneClassesFor(props.tone));
</script>

<template>
  <button
    class="relative flex flex-col items-start justify-start w-full gap-4 px-5 py-5 -m-px text-left transition-all duration-200 ease-in border border-solid cursor-pointer bg-n-solid-1 rounded-2xl border-n-weak group/channel"
    :class="{
      'hover:enabled:border-n-violet-7 hover:enabled:shadow-md disabled:opacity-60 disabled:cursor-not-allowed':
        !isComingSoon,
      'cursor-not-allowed disabled:opacity-80': isComingSoon,
    }"
  >
    <div class="relative">
      <div
        class="flex items-center justify-center size-10 rounded-xl"
        :class="tileClasses"
      >
        <Icon :icon="icon" class="size-5" />
      </div>
      <div
        v-if="hasVoiceBadge"
        class="absolute -top-1 ltr:-right-1 rtl:-left-1 flex size-4 items-center justify-center rounded-full bg-n-alpha-2 ring-2 ring-n-solid-1"
      >
        <Icon icon="i-lucide-audio-lines" class="text-n-slate-10 size-2.5" />
      </div>
    </div>

    <div class="flex flex-col items-start gap-1">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-semibold text-start text-n-slate-12">
          {{ title }}
        </h3>
        <Label
          v-if="isBeta && !isComingSoon"
          v-tooltip.top="t('GENERAL.BETA_DESCRIPTION')"
          :label="t('GENERAL.BETA')"
          color="violet"
          compact
        />
      </div>
      <p class="text-sm leading-relaxed text-start text-n-slate-11">
        {{ description }}
      </p>
    </div>

    <span
      v-if="!isComingSoon"
      class="inline-flex items-center h-8 px-2 -mx-2 mt-auto text-sm font-medium transition-colors rounded-lg text-n-violet-11 group-hover/channel:bg-n-alpha-2"
    >
      {{ t('CHANNEL_SELECTOR.SET_UP') }}
    </span>

    <div
      v-if="isComingSoon"
      class="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] rounded-2xl bg-gradient-to-br from-n-surface-1/90 via-n-surface-1/70 to-n-surface-1/95 cursor-not-allowed"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CHANNEL_SELECTOR.COMING_SOON') }} 🚀
      </span>
    </div>
  </button>
</template>
